/** Weekday identifiers and the persisted schedule grammar are data, not interface copy. */
export const OFFICE_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
export type OfficeDay = typeof OFFICE_DAYS[number];
export interface OfficeTime { hour: string; minute: string; period: 'AM' | 'PM' }
export interface OfficeSchedule { open: OfficeTime; close: OfficeTime; lunch?: { start: OfficeTime; end: OfficeTime } }
export type OfficeWeek = Partial<Record<OfficeDay, OfficeSchedule>>;
export type OfficeTimeKey = 'open' | 'close' | 'lunchStart' | 'lunchEnd';
export interface OfficeIssue { day: OfficeDay; time: OfficeTimeKey; segment: 'hour' | 'minute'; reason: 'hour' | 'minute' | 'order' | 'lunch' }

export const cloneSchedule = (value: OfficeSchedule): OfficeSchedule => ({
  open: { ...value.open }, close: { ...value.close },
  ...(value.lunch ? { lunch: { start: { ...value.lunch.start }, end: { ...value.lunch.end } } } : {}),
});
export const emptyTime = (): OfficeTime => ({ hour: '', minute: '', period: 'AM' });
export const emptySchedule = (): OfficeSchedule => ({ open: emptyTime(), close: emptyTime() });
export const defaultSchedule = (): OfficeSchedule => ({ open: { hour: '8', minute: '00', period: 'AM' }, close: { hour: '5', minute: '00', period: 'PM' } });
/** Restore effective values without introducing a second persisted inheritance contract. */
export function inferSharedHours(week: OfficeWeek): { shared: OfficeSchedule; custom: Set<OfficeDay> } {
  const key = (value: OfficeSchedule): string => JSON.stringify(cloneSchedule(value));
  const days = OFFICE_DAYS.filter(day => week[day]);
  let shared = days.length ? week[days[0]!]! : emptySchedule();
  let largest = 0;
  for (const day of days) {
    const candidate = week[day]!;
    const count = days.filter(other => key(week[other]!) === key(candidate)).length;
    // Strictly greater preserves weekday order for ties, including incomplete values.
    if (count > largest) { shared = candidate; largest = count; }
  }
  return { shared: cloneSchedule(shared), custom: new Set(days.filter(day => key(week[day]!) !== key(shared))) };
}
export function timeMinutes(time: OfficeTime): number | null {
  if (!/^\d{1,2}$/.test(time.hour) || Number(time.hour) < 1 || Number(time.hour) > 12 ||
      !/^\d{1,2}$/.test(time.minute) || Number(time.minute) > 59) return null;
  return (Number(time.hour) % 12 + (time.period === 'PM' ? 12 : 0)) * 60 + Number(time.minute);
}
export function fromMinutes(value: number): OfficeTime {
  const minutes = ((value % 1440) + 1440) % 1440;
  const hour = Math.floor(minutes / 60);
  return { hour: String(hour % 12 || 12), minute: String(minutes % 60).padStart(2, '0'), period: hour < 12 ? 'AM' : 'PM' };
}
/** Prefer lunch at noon, otherwise center a shorter break within the opening hours. */
export function defaultBreak(schedule: OfficeSchedule): NonNullable<OfficeSchedule['lunch']> {
  const open = timeMinutes(schedule.open);
  const close = timeMinutes(schedule.close);
  let start = 12 * 60;
  let duration = 60;
  if (open !== null && close !== null && close - open >= 3 && !(open < start && close > start + duration)) {
    duration = Math.min(60, Math.floor((close - open) / 3));
    if (duration >= 15) duration = Math.floor(duration / 15) * 15;
    const middle = (open + close - duration) / 2;
    const rounded = duration >= 15 ? Math.round(middle / 15) * 15 : Math.floor(middle);
    start = Math.max(open + 1, Math.min(close - duration - 1, rounded));
  }
  // Incomplete/invalid opening hours still need correction by the user.
  return { start: fromMinutes(start), end: fromMinutes(start + duration) };
}
function formatTime(time: OfficeTime): string {
  const minutes = timeMinutes(time);
  if (minutes !== null) return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  // Reserved draft-only token retains incomplete/invalid segments exactly. Validation
  // prevents these tokens reaching generation; review presents a readable summary.
  return `[${encodeURIComponent(time.hour)}|${encodeURIComponent(time.minute)}|${time.period}]`;
}
function parseTime(value: string): OfficeTime | null {
  if (/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)) return fromMinutes(Number(value.slice(0, 2)) * 60 + Number(value.slice(3)));
  const draft = /^\[([^|\[\]]*)\|([^|\[\]]*)\|(AM|PM)\]$/.exec(value);
  if (!draft) return null;
  try { return { hour: decodeURIComponent(draft[1]!), minute: decodeURIComponent(draft[2]!), period: draft[3] as 'AM' | 'PM' }; }
  catch { return null; }
}
export function formatOfficeHours(week: OfficeWeek): string {
  return OFFICE_DAYS.filter(day => week[day]).map(day => {
    const schedule = week[day]!;
    const ranges = schedule.lunch
      ? `${formatTime(schedule.open)}-${formatTime(schedule.lunch.start)}, ${formatTime(schedule.lunch.end)}-${formatTime(schedule.close)}`
      : `${formatTime(schedule.open)}-${formatTime(schedule.close)}`;
    return `${day} ${ranges}`;
  }).join('; ');
}
/** Only generated grammar is interpreted; arbitrary historical text is retained. */
export function parseOfficeHours(value: string): OfficeWeek | null {
  if (!value) return {};
  const week: OfficeWeek = {};
  for (const entry of value.split('; ')) {
    const day = OFFICE_DAYS.find(day => entry.startsWith(`${day} `));
    if (!day || week[day]) return null;
    const ranges = entry.slice(day.length + 1).split(', ');
    if (ranges.length < 1 || ranges.length > 2) return null;
    const times = ranges.flatMap(range => {
      // A hyphen inside an encoded invalid segment must not be a range separator.
      const match = /^(\[[^\]]*\]|\d{2}:\d{2})-(\[[^\]]*\]|\d{2}:\d{2})$/.exec(range);
      return match ? [parseTime(match[1]!), parseTime(match[2]!)] : [null];
    });
    if (times.some(time => !time) || times.length !== ranges.length * 2) return null;
    week[day] = ranges.length === 1 ? { open: times[0]!, close: times[1]! }
      : { open: times[0]!, lunch: { start: times[1]!, end: times[2]! }, close: times[3]! };
  }
  return week;
}
export function scheduleIssue(day: OfficeDay, schedule: OfficeSchedule): OfficeIssue | null {
  const times: [OfficeTimeKey, OfficeTime][] = [['open', schedule.open], ['close', schedule.close]];
  if (schedule.lunch) times.push(['lunchStart', schedule.lunch.start], ['lunchEnd', schedule.lunch.end]);
  for (const [key, value] of times) {
    if (!/^\d{1,2}$/.test(value.hour) || Number(value.hour) < 1 || Number(value.hour) > 12) return { day, time: key, segment: 'hour', reason: 'hour' };
    if (!/^\d{1,2}$/.test(value.minute) || Number(value.minute) > 59) return { day, time: key, segment: 'minute', reason: 'minute' };
  }
  const open = timeMinutes(schedule.open)!;
  const close = timeMinutes(schedule.close)!;
  if (close <= open) return { day, time: 'close', segment: 'hour', reason: 'order' };
  if (schedule.lunch) {
    const start = timeMinutes(schedule.lunch.start)!;
    const end = timeMinutes(schedule.lunch.end)!;
    if (start <= open || start >= close) return { day, time: 'lunchStart', segment: 'hour', reason: 'lunch' };
    if (end <= start || end >= close) return { day, time: 'lunchEnd', segment: 'hour', reason: 'lunch' };
  }
  return null;
}
